import {
	propositionDocumentTypeEnum,
	ProjectDocumentForSignatureTypeKeys,
	SendDocumentForSignatureRequest,
	SignatureMethodValues,
	SEND_DOCUMENT_TYPE_CONFIG,
	PropositionDocumentType,
} from "@dune/types";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
	useSendDocumentForSignature,
	useUploadPropositionDocument,
} from "../../../../api/mutations/propositions.mutations";
import { registerS3File } from "../../../../api/queries/files.queries";
import { useGetPropositionFiles } from "../../../../api/queries/propositions.queries";
interface UseSendDocumentFlowParams {
	type: ProjectDocumentForSignatureTypeKeys;
	open: boolean;
	onOpenChange: (open: boolean) => void;
	propositionId: number;
	onSuccess?: () => void;
}

interface DocumentFile {
	id: string;
	fileId: string;
	fileName: string;
	createdAt: string;
	mimeType: string;
	key: string;
	bucket?: string;
	type: string;
}

type Step = "CHOICE" | "MANUAL" | "ELECTRONIC";

export function useSendDocumentFlow({
	type,
	open,
	onOpenChange,
	propositionId,
	onSuccess,
}: UseSendDocumentFlowParams) {
	const [selectedFileId, setSelectedFileId] = useState<string>("");
	const [signatureMethod, setSignatureMethod] =
		useState<SignatureMethodValues>("ELECTRONIC");
	const [step, setStep] = useState<Step>("CHOICE");
	const [selectOpen, setSelectOpen] = useState(false);

	const [signatoryLastname, setSignatoryLastname] = useState("");
	const [signatoryFirstname, setSignatoryFirstname] = useState("");
	const [signatoryEmail, setSignatoryEmail] = useState("");
	const [signatoryPhone, setSignatoryPhone] = useState("");

	const { data: files, isLoading: isLoadingFiles } =
		useGetPropositionFiles(propositionId);
	const sendDocumentMutation = useSendDocumentForSignature();
	const uploadDocument = useUploadPropositionDocument();

	const config = SEND_DOCUMENT_TYPE_CONFIG[type];
	const documentTypeFilter = config.documentTypeFilter;

	const allDocuments: DocumentFile[] = (files || []).map((doc) => ({
		id: doc.id.toString(),
		fileId: doc.file.id,
		fileName: doc.file.name,
		createdAt: doc.createdAt,
		mimeType: doc.file.mimeType,
		key: doc.file.key,
		type: doc.type,
	}));
	const documents = allDocuments.filter((d) => d.type === documentTypeFilter);

	const selectedDocument = documents.find((d) => d.fileId === selectedFileId);

	const conditionsGenerales = allDocuments.find(
		(p) => p.type === propositionDocumentTypeEnum.CONDITIONS_GENERALES,
	);

	const labels = config.labels;

	useEffect(() => {
		if (documents.length > 0 && !selectedFileId) {
			const firstDocument = documents.find(
				(d) => d.type === documentTypeFilter,
			);
			setSelectedFileId((firstDocument || documents[0]).fileId);
		}
	}, [documents, selectedFileId, documentTypeFilter]);

	useEffect(() => {
		if (!open) {
			setSelectOpen(false);
			setSelectedFileId("");
			setSignatureMethod("ELECTRONIC");
			setStep("CHOICE");
			setSignatoryEmail("");
			setSignatoryFirstname("");
			setSignatoryLastname("");
			setSignatoryPhone("");
		}
	}, [open]);

	const handleNext = () => {
		if (signatureMethod === "MANUAL") {
			setStep("MANUAL");
			return;
		}
		setStep("ELECTRONIC");
	};

	const handleSendDocument = () => {
		if (!selectedFileId) {
			toast.error(
				`Veuillez sélectionner ${config.errorMessages.selectDocument}`,
			);
			return;
		}

		const payload: SendDocumentForSignatureRequest = {
			propositionId: Number(propositionId),
			signatureMethod,
			fileId: selectedFileId,
			documentType: type,
			...(signatureMethod === "ELECTRONIC"
				? {
						signatoryEmail,
						signatoryFirstname,
						signatoryLastname,
						signatoryPhone,
					}
				: {}),
		};

		sendDocumentMutation.mutate(payload, {
			onSuccess: () => {
				toast.success(`${config.successMessage} pour signature avec succès`);
				onOpenChange(false);
				onSuccess?.();
			},
			onError: () => {
				toast.error(`Erreur lors de l'envoi ${config.errorMessages.sendError}`);
			},
		});
	};

	const handleFileSelection = (value: string) => {
		setSelectedFileId(value);
		setTimeout(() => setSelectOpen(false), 50);
	};

	const handleInlineUpload = async (
		file: { bucket: string; key: string; name: string; type: string },
		documentType: PropositionDocumentType,
	) => {
		try {
			const registered = await registerS3File({
				bucket: file.bucket,
				key: file.key,
				name: file.name,
				mimeType: file.type,
			});
			await uploadDocument.mutateAsync({
				propositionId,
				fileId: registered.id,
				type: documentType,
			});
			toast.success("Document ajouté avec succès");
		} catch {
			toast.error("Erreur lors de l'ajout du document");
		}
	};

	const isReady = !isLoadingFiles && documents.length > 0 && selectedFileId;
	const isLoading = sendDocumentMutation.isPending;

	return {
		selectedFileId,
		signatureMethod,
		step,
		selectOpen,
		signatoryLastname,
		signatoryFirstname,
		signatoryEmail,
		signatoryPhone,
		setSelectOpen,
		setSignatureMethod,
		setSignatoryLastname,
		setSignatoryFirstname,
		setSignatoryEmail,
		setSignatoryPhone,
		documents,
		selectedDocument,
		conditionsGenerales,
		isReady,
		isLoading,
		isLoadingFiles,
		handleNext,
		handleSendDocument,
		handleFileSelection,
		handleInlineUpload,
		labels,
	};
}

export type UseSendDocumentFlowReturn = ReturnType<typeof useSendDocumentFlow>;
